import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  getNursingAlertsDetail,
  // 未来扩展：店务、到家预警明细接口
  // getStoreAlertsDetail,
  // getHomeAlertsDetail,
} from '@/service/alerts'

// ==================== 初始状态 ====================

const defaultState = {
  fetching: false,
  error: null,
  query: {},
  // 不同服务类型的数据存储
  data: {
    list: [],
    total: 0,
    pageNumber: 1,
    pageSize: 20
  },
  // 按 serviceType 存储数据
  nursing: { // 护理预警
    list: [],
    total: 0,
    pageNumber: 1,
    pageSize: 20,
    query: {}
  },
  storeService: { // 店务预警
    list: [],
    total: 0,
    pageNumber: 1,
    pageSize: 20,
    query: {}
  },
  homeService: { // 到家预警
    list: [],
    total: 0,
    pageNumber: 1,
    pageSize: 20,
    query: {}
  },
  roomService: { // 客房预警
    list: [],
    total: 0,
    pageNumber: 1,
    pageSize: 20,
    query: {}
  },
  cateringService: { // 餐饮预警
    list: [],
    total: 0,
    pageNumber: 1,
    pageSize: 20,
    query: {}
  },
  monitoringService: { // 监控预警
    list: [],
    total: 0,
    pageNumber: 1,
    pageSize: 20,
    query: {}
  }
}

// ==================== 服务类型映射配置 ====================

/**
 * 服务类型映射函数 - 根据serviceType调用不同的API服务
 * 实现灵活的多服务支持，便于扩展新的API接口
 * @param {string} serviceType - 服务类型标识
 * @param {Object} query - 查询参数
 * @returns {Function} 返回对应的API调用函数
 */
const switchPayload = (serviceType, query) => {
  const payload = {
    nursing: () => getNursingAlertsDetail(query), // 护理预警明细
    store: () => {
      // 未来扩展：店务预警明细接口
      // return getStoreAlertsDetail(query)
      console.warn('Redux: store 预警明细接口尚未实现')
      return Promise.reject(new Error('store 预警明细接口尚未实现'))
    },
    homeService: () => {
      // 未来扩展：到家预警明细接口
      // return getHomeAlertsDetail(query)
      console.warn('Redux: homeService 预警明细接口尚未实现')
      return Promise.reject(new Error('homeService 预警明细接口尚未实现'))
    },
    undefined: () =>
      console.error('Redux: fetchAlertsProfile 请求未定义 serviceType 类型')
  }
  return payload[serviceType] || payload.undefined
}

// ==================== 异步Action ====================

/**
 * 异步获取预警明细的thunk action
 * 支持多服务类型
 * @param {Object} options - 请求选项
 * @param {string} options.serviceType - 服务类型，必填 (nursing, store, homeService)
 * @param {Object} options.query - 查询参数对象
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchAlertsProfile = createAsyncThunk(
  'alertsProfile/fetchAlertsProfile',
  async ({ serviceType, query = {} }, { rejectWithValue }) => {
    try {
      if (!serviceType) {
        return rejectWithValue('serviceType 参数是必需的')
      }

      console.log('[fetchAlertsProfile] 请求参数:', {
        serviceType,
        query
      })

      const response = await switchPayload(serviceType, query)()

      console.log('[fetchAlertsProfile] 响应数据:', {
        serviceType,
        query,
        hasData: !!response.data
      })

      return {
        data: response.data,
        serviceType,
        query
      }
    } catch (error) {
      console.error('[fetchAlertsProfile] 请求失败:', {
        serviceType,
        query,
        error: error.response?.data || error.message
      })
      return rejectWithValue(
        error.response?.data || `获取${serviceType}预警明细失败`
      )
    }
  }
)

// ==================== Redux Slice ====================

const alertsProfileSlice = createSlice({
  name: 'alertsProfile',
  initialState: defaultState,
  reducers: {
    resetAlertsProfileState: (state, action) => {
      const serviceType = action.payload?.serviceType
      if (serviceType && state[serviceType]) {
        state[serviceType] = {
          list: [],
          total: 0,
          pageNumber: 1,
          pageSize: 20,
          query: {}
        }
      } else {
        // 重置所有状态
        state.query = {}
        state.data = {
          list: [],
          total: 0,
          pageNumber: 1,
          pageSize: 20
        }
        Object.keys(defaultState).forEach((key) => {
          if (
            key !== 'fetching' &&
            key !== 'error' &&
            key !== 'query' &&
            key !== 'data'
          ) {
            state[key] = {
              list: [],
              total: 0,
              pageNumber: 1,
              pageSize: 20,
              query: {}
            }
          }
        })
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlertsProfile.pending, (state, action) => {
        state.fetching = true
        state.error = null
        state.query = action.meta.arg.query
      })
      .addCase(fetchAlertsProfile.fulfilled, (state, action) => {
        state.fetching = false
        state.error = null
        const { data, serviceType, query } = action.payload

        // 更新通用 data
        state.data =
          data || {
            list: [],
            total: 0,
            pageNumber: 1,
            pageSize: 20
          }
        state.query = query

        // 按 serviceType 存储数据
        if (serviceType && state[serviceType]) {
          state[serviceType] = {
            ...(data || {
              list: [],
              total: 0,
              pageNumber: 1,
              pageSize: 20
            }),
            query: query
          }
        }
      })
      .addCase(fetchAlertsProfile.rejected, (state, action) => {
        state.fetching = false
        state.error = action.payload
        state.data = {
          list: [],
          total: 0,
          pageNumber: 1,
          pageSize: 20
        }
      })
  }
})

export const { resetAlertsProfileState } = alertsProfileSlice.actions
export default alertsProfileSlice.reducer
